export const DEFAULT_MIN_REPEAT_QUESTION_DISTANCE = 10;

const normalizeCode = (value) => String(value || "").trim();
const normalizeQuestionId = (value) =>
  value === null || value === undefined ? "" : String(value);

export const createQuestionOrderScopeKey = ({ userId, testId, part } = {}) => {
  const normalizedUserId = normalizeQuestionId(userId);
  const normalizedTestId = normalizeQuestionId(testId);
  const normalizedPart = normalizeQuestionId(part);
  if (!normalizedUserId || !normalizedTestId || !normalizedPart) {
    return "";
  }
  return JSON.stringify([normalizedUserId, normalizedTestId, normalizedPart]);
};

export const restorePersistedQuestionOrder = (
  questions = [],
  questionIds = [],
  {
    positionOffset = 0,
    shouldRestorePosition = () => false,
  } = {}
) => {
  const savedPositions = new Map();
  (questionIds || []).forEach((questionId, index) => {
    const key = normalizeQuestionId(questionId);
    if (key && !savedPositions.has(key)) {
      savedPositions.set(key, index);
    }
  });
  if (!savedPositions.size) {
    return [...(questions || [])];
  }

  const normalizedOffset = Number(positionOffset);
  return (questions || [])
    .map((question, index) => ({
      question,
      index,
      savedPosition: savedPositions.get(normalizeQuestionId(question?.id)),
    }))
    .sort((a, b) => {
      const aIsSaved = Number.isInteger(a.savedPosition);
      const bIsSaved = Number.isInteger(b.savedPosition);
      if (aIsSaved && bIsSaved) {
        return a.savedPosition - b.savedPosition;
      }
      if (aIsSaved) return -1;
      if (bIsSaved) return 1;
      return a.index - b.index;
    })
    .map(({ question, savedPosition }) => {
      if (!Number.isInteger(savedPosition) || !shouldRestorePosition(question)) {
        return question;
      }
      return {
        ...question,
        whichPlace:
          (Number.isFinite(normalizedOffset) ? normalizedOffset : 0) +
          savedPosition +
          1,
      };
    });
};

export const isRepeatQuestion = (question) =>
  Boolean(normalizeCode(question?.repeat_question_code));

export const areRepeatPair = (question, otherQuestion) => {
  const questionCode = normalizeCode(question?.question_code);
  const otherQuestionCode = normalizeCode(otherQuestion?.question_code);
  const questionRepeatCode = normalizeCode(question?.repeat_question_code);
  const otherRepeatCode = normalizeCode(otherQuestion?.repeat_question_code);

  return (
    (questionCode && questionCode === otherRepeatCode) ||
    (otherQuestionCode && otherQuestionCode === questionRepeatCode)
  );
};

export const shuffleQuestions = (questions = [], random = Math.random) => {
  const nextQuestions = [...(questions || [])];
  for (let index = nextQuestions.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [nextQuestions[index], nextQuestions[swapIndex]] = [
      nextQuestions[swapIndex],
      nextQuestions[index],
    ];
  }
  return nextQuestions;
};

const getRepeatDistanceAtIndex = (questions, question, questionIndex) => {
  let closestDistance = Infinity;

  questions.forEach((otherQuestion, otherIndex) => {
    if (otherIndex === questionIndex || !areRepeatPair(question, otherQuestion)) {
      return;
    }
    closestDistance = Math.min(
      closestDistance,
      Math.abs(questionIndex - otherIndex)
    );
  });

  return closestDistance;
};

const getRepeatDistanceAfterInsert = (questions, question, insertIndex) => {
  let closestDistance = Infinity;

  questions.forEach((otherQuestion, otherIndex) => {
    if (!areRepeatPair(question, otherQuestion)) {
      return;
    }
    const nextOtherIndex = otherIndex >= insertIndex ? otherIndex + 1 : otherIndex;
    closestDistance = Math.min(
      closestDistance,
      Math.abs(insertIndex - nextOtherIndex)
    );
  });

  return closestDistance;
};

const findBestRepeatInsertIndex = (
  questions,
  question,
  minDistance = DEFAULT_MIN_REPEAT_QUESTION_DISTANCE
) => {
  let bestIndex = questions.length;
  let bestDistance = -1;

  for (let index = 0; index <= questions.length; index += 1) {
    const distance = getRepeatDistanceAfterInsert(questions, question, index);
    if (distance >= minDistance && distance > bestDistance) {
      bestIndex = index;
      bestDistance = distance;
      continue;
    }
    if (bestDistance < minDistance && distance > bestDistance) {
      bestIndex = index;
      bestDistance = distance;
    }
  }

  return bestIndex;
};

export const separateRepeatQuestions = (
  questions = [],
  minDistance = DEFAULT_MIN_REPEAT_QUESTION_DISTANCE
) => {
  const normalQuestions = [];
  const repeatQuestions = [];

  (questions || []).forEach((question) => {
    if (isRepeatQuestion(question)) {
      repeatQuestions.push(question);
      return;
    }
    normalQuestions.push(question);
  });

  return repeatQuestions.reduce((orderedQuestions, repeatQuestion) => {
    const insertIndex = findBestRepeatInsertIndex(
      orderedQuestions,
      repeatQuestion,
      minDistance
    );
    const nextQuestions = [...orderedQuestions];
    nextQuestions.splice(insertIndex, 0, repeatQuestion);
    return nextQuestions;
  }, normalQuestions);
};

const getWhichPlace = (question) => {
  const whichPlace = Number(question?.whichPlace);
  return Number.isFinite(whichPlace) ? whichPlace : Number.MAX_SAFE_INTEGER;
};

const findNextQuestionIndex = (
  placedQuestions,
  candidateQuestions,
  minDistance = DEFAULT_MIN_REPEAT_QUESTION_DISTANCE
) => {
  let fallbackIndex = 0;
  let fallbackDistance = -1;

  for (let index = 0; index < candidateQuestions.length; index += 1) {
    const candidateQuestion = candidateQuestions[index];
    const candidateIndex = placedQuestions.length;
    const distance = getRepeatDistanceAtIndex(
      [...placedQuestions, candidateQuestion],
      candidateQuestion,
      candidateIndex
    );

    if (distance >= minDistance) {
      return index;
    }
    if (distance > fallbackDistance) {
      fallbackIndex = index;
      fallbackDistance = distance;
    }
  }

  return fallbackIndex;
};

export const orderQuestionsForResume = (
  questions = [],
  isFinishedQuestion = () => false,
  minDistance = DEFAULT_MIN_REPEAT_QUESTION_DISTANCE
) => {
  const questionsWithIndex = (questions || []).map((question, index) => ({
    question,
    index,
  }));
  const finishedQuestions = questionsWithIndex
    .filter(({ question }) => isFinishedQuestion(question))
    .sort(
      (a, b) =>
        getWhichPlace(a.question) - getWhichPlace(b.question) || a.index - b.index
    )
    .map(({ question }) => question);
  const pendingQuestions = questionsWithIndex
    .filter(({ question }) => !isFinishedQuestion(question))
    .map(({ question }) => question);
  const orderedQuestions = [...finishedQuestions];
  const remainingQuestions = [...pendingQuestions];

  while (remainingQuestions.length) {
    const nextIndex = findNextQuestionIndex(
      orderedQuestions,
      remainingQuestions,
      minDistance
    );
    const [nextQuestion] = remainingQuestions.splice(nextIndex, 1);
    orderedQuestions.push(nextQuestion);
  }

  return orderedQuestions;
};
